import { Text } from '@chakra-ui/react';
import { find, propEq } from 'ramda';

import { ValidationErrorMessage } from '../../../components/file-upload-dialog/file-upload-dialog';
import {
  ValidatedFilename,
  ValidatedFilenameErrorCode,
  ValidatedFilenameErrorFragment,
  ValidateFilenamesMutationVariables,
} from '../../../graphql/generated';
import { createFile, EnhancedFile } from '../../../utils/file/file';

type ValidatedFilenameTuple = [
  ValidatedFilename[],
  [ValidatedFilename[], ValidatedFilename[], ValidatedFilename[]]
];

type TransformationConfig = {
  validFiles: ValidatedFilename[];
  invalidFileConfig: Record<ValidatedFilenameErrorCode, ValidatedFilename[]>;
};

/**
 * @name createTupleOfValidInvalidFiles
 * @description
 * Takes an Array<ValidatedFilename> and returns a nested array of
 * valid and invalid files
 */
export const createTupleOfValidInvalidFiles = (
  validateFilenames: ValidatedFilename[]
): ValidatedFilenameTuple =>
  validateFilenames.reduce<ValidatedFilenameTuple>(
    (acc, validationResult) => {
      if (validationResult.isValid) {
        return [[...acc[0], validationResult], acc[1]];
      }

      const isFilenameLengthLimitError = !!validationResult.errors?.some(
        (x) => x.code === ValidatedFilenameErrorCode.FILENAME_LENGTH_LIMIT
      );
      const isInvalidCharacterError = !!validationResult.errors?.some(
        (x) => x.code === ValidatedFilenameErrorCode.INVALID_CHARACTERS
      );
      const isUnsupportedFilenameError = !!validationResult.errors?.some(
        (x) => x.code === ValidatedFilenameErrorCode.UNSUPPORTED_FILE_TYPE
      );



      if (isFilenameLengthLimitError) {
        return [acc[0], [[...acc[1][0], validationResult], acc[1][1], acc[1][2]]];
      }

      if (isInvalidCharacterError) {
        return [acc[0], [acc[1][0], [...acc[1][1], validationResult], acc[1][2]]];
      }

      if (isUnsupportedFilenameError) {
        return [acc[0], [acc[1][0], acc[1][1], [...acc[1][2], validationResult]]];
      }

      return acc;
    },
    [[], [[], [], []]]
  );

/**
 * @name transformFilesUsingFileValidationResult
 * @description
 * Takes an Array<EnhancedFile> and TransformationConfig and checks each file against
 * the list of valid and invalid files.
 */
export const transformFilesUsingFileValidationResult = (
  files: EnhancedFile[],
  config: TransformationConfig
): EnhancedFile[] => {
  const { validFiles, invalidFileConfig } = config;

  return files.reduce<EnhancedFile[]>((acc, file) => {
    //1 Remove files with file length error
    const hasLengthError = invalidFileConfig.FILENAME_LENGTH_LIMIT.find(
      ({ id }) => id === file.id
    );
    const hasUnsupportedFilenameError = invalidFileConfig.UNSUPPORTED_FILE_TYPE.find(
      ({ id }) => id === file.id
    );
    if (hasLengthError || hasUnsupportedFilenameError) {
      // if file has length error or is file type is not supported, don't add to accumulator
      return acc;
    }

    //2 Rename files that have invalid chars and add to accumulator
    const hasInvalidChars = invalidFileConfig.INVALID_CHARACTERS.find(
      ({ id }) => id === file.id
    );
    if (hasInvalidChars) {
      if (!hasInvalidChars.errors) throw new Error('Error renaming file');

      const [filepath, filename] = getSuggestedFilepathAndFilename(
        hasInvalidChars.errors
      );
      

      
      const renamedEnhancedFile: EnhancedFile = {
        id: file.id,
        path: filepath,
        file: createFile(file.file, filename, file.file.type),
      };

      return [...acc, renamedEnhancedFile];
    }

    //3 Check if file is in list of valid files and add it to the accumulator, if it's not already in it
    const isValidFile = validFiles.find(({ id }) => id === file.id);
    const isValidFileInAcc = acc.some(({ id }) => id === isValidFile?.id);

    if (isValidFile && !isValidFileInAcc) {
      return [...acc, file];
    }

    return acc;
  }, []);
};

/**
 * @name getSuggestedFilepathAndFilename
 * @description
 * Takes an Array<ValidatedFilenameErrorFragment> and finds errors with code
 * `INVALID_CHARACTERS`. If found, it returns the suggested file path and file name
 */
export const getSuggestedFilepathAndFilename = (
  errors: ValidatedFilenameErrorFragment[]
): [string, string] => {
  const error = find(
    propEq('code', ValidatedFilenameErrorCode.INVALID_CHARACTERS),
    errors
  );

  if (!error) throw new Error('Error getting suggested fileoath');

  const suggestedFilepath = error.suggestedFilepath as string;
  const suggestedFilename = error.suggestedFilename as string;

  return [suggestedFilepath, suggestedFilename];
};

/**
 * @name buildInvalidCharErrors
 * @description
 * Takes an Array<ValidatedFilename> and wraps each ValidatedFilename
 * in an object containing id, and invalid char message
 */
export const buildInvalidCharErrors = (
  filesWithInvalidChars: ValidatedFilename[]
): Array<ValidationErrorMessage> => {
  return filesWithInvalidChars.map((file) => ({
    id: file.id,
    message: (
      <>
        <Text as="span" fontWeight="semibold">
          {file.filepath}{' '}
        </Text>
        <Text as="span">will be renamed to </Text>
        <Text as="span" fontWeight="semibold">
          {file.errors ? getSuggestedFilepathAndFilename(file.errors)[0] : ''}
        </Text>
      </>
    ),
  }));
};

/**
 * @name buildLengthErrors
 * @description
 * Takes an Array<ValidatedFilename> and wraps each ValidatedFilename
 * in an object containing id, and invalid name length message
 */
export const buildLengthErrors = (
  filesWithLengthError: ValidatedFilename[]
): Array<ValidationErrorMessage> => {
  return filesWithLengthError.map((file) => ({
    id: file.id,
    message: (
      <>
        <Text as="span" fontWeight="semibold">
          {file.filepath}{' '}
        </Text>
        <Text as="span">will not be uploaded</Text>
      </>
    ),
  }));
};

export const buildUnsupportedFilenameErrors = (
  filesWithUnsupportedFilenameError: ValidatedFilename[]
): Array<ValidationErrorMessage> => {
  return filesWithUnsupportedFilenameError.map((file) => ({
    id: file.id,
    message: (
      <>
        <Text as="span" fontWeight="semibold">
          {file.filepath}{' '}
        </Text>
        <Text as="span">
          is not a supported file type.
          {file.filepath.split('.').pop() === 'zip' &&
            ' You can extract the zip and upload the entire directory.'}
        </Text>
      </>
    ),
  }));
};

export const getValidateFilesMutationInput = (
  cedantId: string,
  files: EnhancedFile[]
): ValidateFilenamesMutationVariables => ({
  cedant: cedantId,
  input: {
    files: files.map((enhancedFile) => {
      const id = enhancedFile.id;
      const filepath = enhancedFile.path;

      return {
        id,
        filepath,
      };
    }),
  },
});
